import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Fingerprint, Smartphone, CheckCircle, AlertCircle, Loader2, ArrowRight, RefreshCw, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// شعار نفاذ
const NafathLogo = ({ className = "w-12 h-12" }) => (
  <svg viewBox="0 0 100 100" className={className}>
    <circle cx="50" cy="50" r="48" fill="#00A651"/>
    <text x="50" y="40" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">نفاذ</text>
    <text x="50" y="65" textAnchor="middle" fill="white" fontSize="12">Nafath</text>
  </svg>
);

export function NafathLogin({ onSuccess, onError, onCancel, mode = 'login' }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('init'); // init, waiting, success, error, timeout
  const [nationalId, setNationalId] = useState('');
  const [transId, setTransId] = useState('');
  const [randomNumber, setRandomNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(120);

  // عداد تنازلي
  useEffect(() => {
    let timer;
    if (step === 'waiting' && countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setStep('timeout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  // التحقق من حالة الطلب
  const checkStatus = useCallback(async () => {
    if (!nationalId || !transId || !randomNumber) return;
    
    try {
      const response = await axios.post(`${API}/auth/nafath/status`, {
        national_id: nationalId,
        trans_id: transId,
        random: String(randomNumber)
      });
      
      const result = response.data;
      
      if (result.status === 'COMPLETED') {
        setStep('success');
        
        // تسجيل الدخول أو التسجيل
        try {
          const authEndpoint = mode === 'register' 
            ? `${API}/auth/nafath/register` 
            : `${API}/auth/nafath/login`;
          
          const authResponse = await axios.post(authEndpoint, {
            national_id: nationalId,
            trans_id: transId,
            random: String(randomNumber)
          });
          
          if (authResponse.data.user) {
            localStorage.setItem('user', JSON.stringify(authResponse.data.user));
            if (authResponse.data.csrf_token) {
              localStorage.setItem('token', authResponse.data.csrf_token);
            }
            
            if (onSuccess) {
              onSuccess(authResponse.data.user);
            } else {
              setTimeout(() => navigate('/dashboard'), 500);
            }
          }
        } catch (authError) {
          setErrorMessage(authError.response?.data?.detail || 'خطأ في المصادقة');
          setStep('error');
          if (onError) onError(authError.response?.data?.detail);
        }
        
      } else if (result.status === 'REJECTED') {
        setStep('error');
        setErrorMessage('تم رفض طلب التحقق من المستخدم');
        if (onError) onError('تم رفض طلب التحقق');
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  }, [nationalId, transId, randomNumber, mode, navigate, onSuccess, onError]);

  // Polling للتحقق من الحالة
  useEffect(() => {
    let pollInterval;
    if (step === 'waiting') {
      pollInterval = setInterval(checkStatus, 3000);
    }
    return () => clearInterval(pollInterval);
  }, [step, checkStatus]);

  const initiateNafath = async () => {
    if (!nationalId || nationalId.length !== 10) {
      setErrorMessage('يرجى إدخال رقم هوية صحيح (10 أرقام)');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await axios.post(`${API}/auth/nafath/request`, {
        national_id: nationalId
      });

      const result = response.data;

      if (result.success) {
        setTransId(result.trans_id);
        setRandomNumber(result.random);
        setStep('waiting');
        setCountdown(120);
      } else {
        setStep('error');
        setErrorMessage(result.message || 'فشل في بدء عملية التحقق');
        if (onError) onError(result.message);
      }
    } catch (error) {
      console.error('Nafath initiation error:', error);
      setStep('error');
      setErrorMessage(error.response?.data?.detail || 'فشل الاتصال بخدمة نفاذ');
      if (onError) onError(error.response?.data?.detail);
    }
    setLoading(false);
  };

  const resetProcess = () => {
    setStep('init');
    setNationalId('');
    setTransId('');
    setRandomNumber(null);
    setErrorMessage('');
    setCountdown(120);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden max-w-md mx-auto" dir="rtl">
      {/* الهيدر */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white text-center relative">
        {onCancel && (
          <button 
            onClick={onCancel}
            className="absolute top-4 left-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        <NafathLogo className="w-16 h-16 mx-auto mb-3" />
        <h2 className="text-xl font-bold">
          {mode === 'register' ? 'التسجيل عبر نفاذ' : 'الدخول عبر نفاذ'}
        </h2>
        <p className="text-green-100 text-sm">النفاذ الوطني الموحد</p>
      </div>

      <div className="p-6">
        {/* المرحلة الأولى - إدخال رقم الهوية */}
        {step === 'init' && (
          <div className="space-y-6">
            <div className="text-center">
              <Fingerprint className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400">
                أدخل رقم الهوية الوطنية للتحقق من هويتك
              </p>
            </div>

            {errorMessage && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{errorMessage}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 dark:text-gray-300">
                رقم الهوية الوطنية
              </label>
              <input
                type="text"
                value={nationalId}
                onChange={(e) => setNationalId(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="أدخل رقم الهوية (10 أرقام)"
                className="w-full px-4 py-3 border-2 rounded-lg text-center text-lg tracking-wider font-mono dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:border-green-500 focus:ring-2 focus:ring-green-200"
                maxLength={10}
                dir="ltr"
                data-testid="nafath-national-id"
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                {nationalId.length}/10 أرقام
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 dark:text-blue-400 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                كيف يعمل نفاذ؟
              </h4>
              <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-1 list-decimal list-inside">
                <li>أدخل رقم هويتك الوطنية</li>
                <li>سيظهر لك رقم عشوائي</li>
                <li>افتح تطبيق نفاذ على جوالك</li>
                <li>اختر نفس الرقم للتأكيد</li>
              </ol>
            </div>

            <div className="flex gap-3">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="flex-1 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-white transition-colors"
                >
                  إلغاء
                </button>
              )}
              <button
                onClick={initiateNafath}
                disabled={nationalId.length !== 10 || loading}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                data-testid="nafath-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ArrowRight className="w-5 h-5 rotate-180" />
                )}
                متابعة
              </button>
            </div>
          </div>
        )}

        {/* المرحلة الثانية - انتظار التأكيد */}
        {step === 'waiting' && (
          <div className="space-y-6 text-center">
            <div className="relative">
              <Smartphone className="w-20 h-20 text-green-600 mx-auto animate-pulse" />
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
                <span className="text-sm font-bold">!</span>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
                افتح تطبيق نفاذ
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                واختر الرقم التالي للتأكيد:
              </p>
            </div>

            {/* الرقم العشوائي */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 shadow-lg">
              <span className="text-6xl font-bold text-white tracking-widest">
                {randomNumber}
              </span>
            </div>

            {/* العداد */}
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
              <span>ينتهي الطلب خلال</span>
              <span className={`font-mono font-bold ${countdown < 30 ? 'text-red-500' : 'text-green-600'}`}>
                {formatTime(countdown)}
              </span>
            </div>

            {/* شريط التقدم */}
            <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-1000"
                style={{ width: `${(countdown / 120) * 100}%` }}
              />
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              جاري انتظار التأكيد...
            </div>

            <button
              onClick={resetProcess}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm flex items-center justify-center gap-1 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* حالة النجاح */}
        {step === 'success' && (
          <div className="space-y-6 text-center py-6">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-green-600 mb-2">
                تم التحقق بنجاح!
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                جاري تسجيل الدخول...
              </p>
            </div>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
          </div>
        )}

        {/* حالة الخطأ */}
        {step === 'error' && (
          <div className="space-y-6 text-center py-6">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-red-600 mb-2">
                فشل التحقق
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {errorMessage || 'حدث خطأ أثناء عملية التحقق'}
              </p>
            </div>
            <button
              onClick={resetProcess}
              className="px-6 py-3 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              إعادة المحاولة
            </button>
          </div>
        )}

        {/* حالة انتهاء الوقت */}
        {step === 'timeout' && (
          <div className="space-y-6 text-center py-6">
            <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-12 h-12 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-yellow-600 mb-2">
                انتهت المهلة
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                لم يتم التأكيد في الوقت المحدد
              </p>
            </div>
            <button
              onClick={resetProcess}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              إعادة المحاولة
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// زر نفاذ للاستخدام في صفحات أخرى
export function NafathLoginButton({ onSuccess, onError, className = '', mode = 'login' }) {
  const [showModal, setShowModal] = useState(false);

  const handleSuccess = (user) => {
    setShowModal(false);
    if (onSuccess) onSuccess(user);
  };

  const handleError = (error) => {
    if (onError) onError(error);
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center justify-center gap-3 w-full py-3 px-4 border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-all font-medium ${className}`}
        data-testid="nafath-login-btn"
      >
        <NafathLogo className="w-6 h-6" />
        <span>{mode === 'register' ? 'التسجيل عبر نفاذ' : 'الدخول عبر نفاذ'}</span>
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md">
            <NafathLogin 
              mode={mode}
              onSuccess={handleSuccess}
              onError={handleError}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default NafathLogin;
